import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SignUpStep2 from "./SignUpStep2";
import SignUpStep3 from "./SignUpStep3";
import { User, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";

export default function ProfileLayout() {
    const navigate = useNavigate();
    const { completeProfile, user, refreshAuthState } = useAuth();
    const [profileData, setProfileData] = useState({});
    const [currentStep, setCurrentStep] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);

    // Load existing profile data when component mounts (for editing)
    useEffect(() => {
        const loadExistingProfile = async () => {
            try {
                // Try to fetch from API first (most up-to-date)
                try {
                    const response = await api.getProfile();
                    if (response && response.user_data) {
                        const u = response.user_data;
                        // Pre-fill form with existing user data from API
                        const existingData = {
                            first_name: u.first_name || '',
                            middle_name: u.middle_name || '',
                            last_name: u.last_name || '',
                            dateofbirth: u.dateofbirth || '',
                            address: u.address || u.correspondenceAddress || '',
                            phone_number: u.phone_number || '',
                            altMobile: u.alt_phone_number || '',
                            fathersName: u.fathersName || '',
                            mothersName: u.mothersName || '',
                            permanentAddress: u.permanentAddress || '',
                            correspondenceAddress: u.correspondenceAddress || '',
                            email: u.email || '',
                            mother_tongue: u.motherTongue || '',
                            changed_name: u.hasChangedName || '',
                            changedNameDetail: u.changedNameDetail || '',
                            gender: u.gender || '',
                            education: u.highest_education || '',
                            profession: (u.extra_details && Array.isArray(u.extra_details)) 
                                ? u.extra_details.find(entry => entry && entry.type === 'profession')?.data || []
                                : [],
                            category: u.category || '',
                            is_disabled: u.disability ? 'Yes' : 'No',
                            marital_status: u.maritalStatus || '',
                            religion: u.religion || '',
                            nationality: u.nationality || '',
                            domicile_state: u.domicileState || '',
                            district: u.district || '',
                            mandal: u.mandal || '',
                            pincode: u.pincode || '',
                        };
                        console.log('✅ Loaded profile data for editing:', existingData);
                        setProfileData(existingData);
                        setIsLoadingProfile(false);
                        return;
                    }
                } catch (apiError) {
                    console.warn('Could not fetch from API, using localStorage:', apiError);
                }
                
                // Fallback to localStorage
                const currentUser = localStorage.getItem("currentUser");
                if (currentUser) {
                    const userObj = JSON.parse(currentUser);
                    // Pre-fill form with existing user data
                    const existingData = {
                        first_name: userObj.first_name || '',
                        middle_name: userObj.middle_name || '',
                        last_name: userObj.last_name || '',
                        dateofbirth: userObj.dateofbirth || '',
                        address: userObj.address || userObj.correspondenceAddress || '',
                        phone_number: userObj.phone_number || '',
                        altMobile: userObj.alt_phone_number || '',
                        fathersName: userObj.fathersName || '',
                        mothersName: userObj.mothersName || '',
                        permanentAddress: userObj.permanentAddress || '',
                        correspondenceAddress: userObj.correspondenceAddress || '',
                        email: userObj.email || '',
                        mother_tongue: userObj.motherTongue || '',
                        changed_name: userObj.hasChangedName || '',
                        changedNameDetail: userObj.changedNameDetail || '',
                        gender: userObj.gender || '',
                        education: userObj.highest_education || '',
                        profession: (userObj.extra_details && Array.isArray(userObj.extra_details))
                            ? userObj.extra_details.find(entry => entry && entry.type === 'profession')?.data || []
                            : [],
                        category: userObj.category || '',
                        is_disabled: userObj.disability ? 'Yes' : 'No',
                        marital_status: userObj.maritalStatus || '',
                        religion: userObj.religion || '',
                        nationality: userObj.nationality || '',
                        domicile_state: userObj.domicileState || '',
                        district: userObj.district || '',
                        mandal: userObj.mandal || '',
                        pincode: userObj.pincode || '',
                    };
                    setProfileData(existingData);
                }
            } catch (err) {
                console.error('Error loading existing profile:', err);
            } finally {
                setIsLoadingProfile(false);
            }
        };
        loadExistingProfile();
    }, []);

    // Required fields for each step (only required fields, optional fields are not included)
    const step1RequiredFields = ["first_name", "last_name", "dateofbirth", "address", "phone_number", "fathersName", "mothersName", "permanentAddress"];
    const step2RequiredFields = ["gender", "education", "profession"];
    const step3RequiredFields = ["category", "marital_status", "religion", "nationality", "domicile_state", "district", "pincode"];

    // All fields for progress tracking (includes optional fields)
    const step1Fields = ["first_name", "last_name", "dateofbirth", "address", "phone_number", "fathersName", "mothersName", "permanentAddress", "correspondenceAddress", "altMobile"];
    const step2Fields = ["mother_tongue", "changed_name", "gender", "education"];
    const step3Fields = [
        "category", "is_disabled", "marital_status", "religion",
        "nationality", "domicile_state", "district", "mandal", "pincode"
    ];

    const allFields = [...step1Fields, ...step2Fields, ...step3Fields];

    // Validation function to check if required fields for a step are filled
    const validateStep = (step) => {
        const requiredFields = step === 1 ? step1RequiredFields : step === 2 ? step2RequiredFields : step3RequiredFields;
        const errors = {};
        
        requiredFields.forEach(field => {
            const value = profileData[field];
            // Special handling for profession (it's an array)
            if (field === 'profession') {
                if (!value || !Array.isArray(value) || value.length === 0) {
                    errors[field] = 'Profession is required';
                }
            } else {
                // Check if field is empty or just whitespace
                if (!value || (typeof value === 'string' && !value.trim())) {
                    errors[field] = `${field.replace(/_/g, ' ')} is required`;
                }
            }
        });
        
        return errors;
    };

    // Check if current step is valid (without setting state - used for disabled prop)
    const isCurrentStepValid = React.useMemo(() => {
        const errors = validateStep(currentStep);
        return Object.keys(errors).length === 0;
    }, [currentStep, profileData]);

    // Check if all required fields across all steps are filled (without setting state)
    const isAllStepsValid = React.useMemo(() => {
        const step1Errors = validateStep(1);
        const step2Errors = validateStep(2);
        const step3Errors = validateStep(3);
        return Object.keys(step1Errors).length === 0 && 
               Object.keys(step2Errors).length === 0 && 
               Object.keys(step3Errors).length === 0;
    }, [profileData]);

    // Handle next step with validation
    const handleNext = () => {
        const errors = validateStep(currentStep);
        if (Object.keys(errors).length === 0) {
            setValidationErrors({});
            setError('');
            setCurrentStep(prev => Math.min(prev + 1, 3));
        } else {
            setValidationErrors(errors);
            setError('Please fill all required fields before proceeding.');
        }
    };

    // Minimal Progress Component
    const MinimalProgress = () => {
        const isStepComplete = (fields) => fields.every(field => profileData[field]?.trim());

        const renderSingleDash = (fields, label, stepNum) => {
            const isComplete = isStepComplete(fields);
            const isActive = currentStep === stepNum;
            const isPast = currentStep > stepNum;

            return (
                <div key={stepNum} className="flex flex-col gap-1.5 flex-1">
                    <div
                        className={`h-1.5 rounded-full transition-all duration-500 ${isComplete || isPast ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : isActive ? 'bg-blue-400 w-full' : 'bg-gray-200'}`}
                    />
                    <span className={`text-[10px] font-bold uppercase tracking-widest text-center transition-colors duration-300 ${isActive ? 'text-blue-600' : isComplete || isPast ? 'text-green-600' : 'text-gray-400'}`}>
                        {label}
                    </span>
                </div>
            );
        };

        return (
            <div className="flex gap-4 mb-6 px-2">
                {renderSingleDash(step1Fields, "page 1", 1)}
                {renderSingleDash(step2Fields, "page 2", 2)}
                {renderSingleDash(step3Fields, "page 3", 3)}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">Complete Profile</h1>
                    <p className="text-sm text-gray-600 font-medium">Give details once and use it anywhere.</p>
                </div>

                {/* Minimal Progress */}
                <div className="mb-4">
                    <MinimalProgress />
                </div>

                {/* Profile Form Card */}
                <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-6 border border-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl">
                                {currentStep === 1 ? <User className="w-4 h-4 text-blue-600" /> : <CheckCircle className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {currentStep === 1 && "Personal Information"}
                                    {currentStep === 2 && "Identity & Education"}
                                    {currentStep === 3 && "Social & Geographic"}
                                </h3>
                                <p className="text-[10px] text-gray-500 font-medium">Page {currentStep} of 3</p>
                            </div>
                        </div>

                        {/* Pagination Dots (Simplified) */}
                        <div className="flex gap-2">
                            {[1, 2, 3].map(step => (
                                <button
                                    key={step}
                                    onClick={() => setCurrentStep(step)}
                                    className={`h-2 rounded-full transition-all duration-300 ${currentStep === step ? 'bg-blue-600 w-6' : 'bg-gray-200 w-2 hover:bg-gray-300'}`}
                                />
                            ))}
                        </div>
                    </div>

                    {isLoadingProfile ? (
                        <div className="min-h-[350px] flex items-center justify-center">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading profile data...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-[350px]">
                        {currentStep === 1 && (
                            <SignUpStep2
                                key={`step1-${Object.keys(profileData).length > 0 ? 'loaded' : 'empty'}`}
                                onFormChange={(data) => {
                                    setProfileData(prev => ({ ...prev, ...data }));
                                    // Clear validation errors when user updates fields
                                    if (Object.keys(validationErrors).length > 0) {
                                        setValidationErrors({});
                                        setError('');
                                    }
                                }}
                                isMultiStep={true}
                                validationErrors={validationErrors}
                                initialData={profileData}
                            />
                        )}
                        {currentStep === 2 && (
                            <SignUpStep3
                                formData={profileData}
                                visibleGroups={[0, 1]}
                                handleChange={(e) => {
                                    const { name, value } = e.target;
                                    setProfileData(prev => ({ ...prev, [name]: value }));
                                    // Clear validation errors when user updates fields
                                    if (Object.keys(validationErrors).length > 0) {
                                        setValidationErrors({});
                                        setError('');
                                    }
                                }}
                                validationErrors={validationErrors}
                            />
                        )}
                        {currentStep === 3 && (
                            <SignUpStep3
                                formData={profileData}
                                visibleGroups={[2, 3]}
                                handleChange={(e) => {
                                    const { name, value } = e.target;
                                    setProfileData(prev => ({ ...prev, [name]: value }));
                                    // Clear validation errors when user updates fields
                                    if (Object.keys(validationErrors).length > 0) {
                                        setValidationErrors({});
                                        setError('');
                                    }
                                }}
                                validationErrors={validationErrors}
                            />
                        )}
                        </div>
                    )}

                    {/* Form Navigation Buttons */}
                    <div className="mt-6 pt-4 border-t border-gray-50 flex justify-between gap-4">
                        {currentStep > 1 ? (
                            <button
                                onClick={() => setCurrentStep(prev => prev - 1)}
                                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-100 text-gray-600 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-[0.98] text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                        ) : (
                            <div className="flex-1" />
                        )}

                        {currentStep < 3 ? (
                            <button
                                onClick={handleNext}
                                className="flex-[1.5] flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!isCurrentStepValid}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                className="flex-[1.5] bg-blue-600 text-white py-3 rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={async () => {
                                    // Validate all steps before saving
                                    const step1Errors = validateStep(1);
                                    const step2Errors = validateStep(2);
                                    const step3Errors = validateStep(3);
                                    const allErrors = { ...step1Errors, ...step2Errors, ...step3Errors };
                                    
                                    if (Object.keys(allErrors).length > 0) {
                                        setError('Please fill all required fields in all steps before completing your profile.');
                                        setValidationErrors(allErrors);
                                        return;
                                    }
                                    
                                    setValidationErrors({});
                                    setError('');
                                    console.log("Final Save", profileData);
                                    setIsSaving(true);
                                    setError('');
                                    
                                    try {
                                        // Get user email
                                        let email = user?.email;
                                        if (!email) {
                                            const savedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
                                            email = savedUser.email;
                                        }
                                        if (!email) {
                                            throw new Error("Email not found. Please log in again.");
                                        }
                                        
                                        // Prepare profile data with email - ensure all fields are properly formatted
                                        const profileDataWithEmail = {
                                            ...profileData,
                                            email: email
                                        };
                                        
                                        // Map frontend field names to backend field names if needed
                                        // The backend update_data function handles most mappings, but let's ensure consistency
                                        const backendData = { ...profileDataWithEmail };
                                        
                                        // Map address field to correspondenceAddress if permanentAddress exists
                                        if (backendData.permanentAddress && backendData.address && !backendData.correspondenceAddress) {
                                            backendData.correspondenceAddress = backendData.address;
                                        }
                                        
                                        console.log("Saving profile data to backend:", backendData);
                                        
                                        // Call the API to update profile
                                        const result = await completeProfile(backendData);
                                        
                                        if (result.success) {
                                            console.log("Profile saved successfully, redirecting to home...");
                                            
                                            // Save profile data to localStorage for other components to access
                                            localStorage.setItem('userProfile', JSON.stringify(profileData));
                                            
                                            // The completeProfile function already updates currentUser and sets isSignUp2
                                            // But let's ensure profile completion flag is set
                                            localStorage.setItem('isSignUp2', 'true');
                                            
                                            // Refresh auth state to fetch latest data from backend and update profile completion status
                                            if (refreshAuthState) {
                                                try {
                                                    await refreshAuthState();
                                                } catch (refreshError) {
                                                    console.warn("Auth refresh error (non-critical):", refreshError);
                                                }
                                            }
                                            
                                            // Use navigate instead of window.location.href for proper React Router navigation
                                            // The replace: true ensures user can't go back to the signup page
                                            navigate('/', { replace: true });
                                        } else {
                                            setError(result.message || "Profile update failed. Please try again.");
                                        }
                                    } catch (err) {
                                        console.error("Profile save error:", err);
                                        setError(err.message || "An error occurred. Please try again.");
                                    } finally {
                                        setIsSaving(false);
                                    }
                                }}
                                disabled={isSaving || !isAllStepsValid}
                            >
                                {isSaving ? "Saving..." : "Complete Profile"}
                            </button>
                        )}
                    </div>
                    
                    {/* Validation Errors */}
                    {Object.keys(validationErrors).length > 0 && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm font-semibold text-yellow-800 mb-2">Please fill the following required fields:</p>
                            <ul className="list-disc list-inside text-xs text-yellow-700 space-y-1">
                                {Object.values(validationErrors).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {/* Error Message */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}
                </div>

                {/* Privacy Footer */}
                <p className="mt-4 text-center text-[10px] text-gray-400 font-medium">
                    Your data is secure and encrypted. Need help? <span className="text-blue-500 cursor-pointer hover:underline">Contact Support</span>
                </p>
            </div>

            <style jsx>{`
                .min-h-screen {
                    scroll-behavior: smooth;
                }
            `}</style>
        </div>
    );
}